import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  ArrowLeft,
  User,
  Calendar,
  CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import Price from '../components/Price';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminWithdrawals = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingRequest, setProcessingRequest] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchRequests();
  }, [user, navigate]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/admin/withdrawal-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching withdrawal requests:', error);
      toast.error('Failed to load withdrawal requests');
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async (requestId, status) => {
    const token = localStorage.getItem('session_token');
    try {
      await axios.put(
        `${API}/admin/withdrawal-requests/${requestId}`,
        {
          status,
          admin_notes: adminNotes
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(`Withdrawal ${status}`);
      setProcessingRequest(null);
      setAdminNotes('');
      fetchRequests();
    } catch (error) {
      console.error('Error processing withdrawal:', error);
      toast.error(error.response?.data?.detail || 'Failed to process request');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-900/30 text-yellow-400 border-yellow-500/30';
      case 'approved': return 'bg-green-900/30 text-green-400 border-green-500/30';
      case 'rejected': return 'bg-red-900/30 text-red-400 border-red-500/30';
      default: return 'bg-gray-900/30 text-gray-400 border-gray-500/30';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-5 h-5 text-yellow-400" />;
      case 'approved': return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'rejected': return <XCircle className="w-5 h-5 text-red-400" />;
      default: return null;
    }
  };

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const processedRequests = requests.filter(r => r.status !== 'pending');

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-8">
          <div className="container mx-auto px-4">
            <div className="text-white text-center py-8">Loading withdrawal requests...</div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/admin')}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <DollarSign className="w-8 h-8 text-green-400" />
                Withdrawal Requests
              </h1>
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <Card className="glass-dark border-yellow-500/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Pending</p>
                    <p className="text-3xl font-bold text-yellow-400">{pendingRequests.length}</p>
                  </div>
                  <Clock className="w-12 h-12 text-yellow-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="glass-dark border-green-500/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Approved</p>
                    <p className="text-3xl font-bold text-green-400">
                      {processedRequests.filter(r => r.status === 'approved').length}
                    </p>
                  </div>
                  <CheckCircle className="w-12 h-12 text-green-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="glass-dark border-red-500/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-400 text-sm">Rejected</p>
                    <p className="text-3xl font-bold text-red-400">
                      {processedRequests.filter(r => r.status === 'rejected').length}
                    </p>
                  </div>
                  <XCircle className="w-12 h-12 text-red-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Pending Requests */}
          {pendingRequests.length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">Pending Requests</h2>
              <div className="space-y-4">
                {pendingRequests.map((request) => (
                  <Card key={request.id} className="glass-dark border-yellow-500/30 bg-yellow-900/10">
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                {getStatusIcon(request.status)}
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(request.status)}`}>
                                  {request.status.toUpperCase()}
                                </span>
                              </div>
                              <h3 className="text-white text-lg font-semibold">{request.tipster_name}</h3>
                              <p className="text-gray-400 text-sm">{request.tipster_email}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-3xl font-bold text-green-400">
                                <Price amount={request.amount || 0} />
                              </p>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div className="bg-white/5 rounded p-3">
                              <p className="text-gray-400 text-xs mb-1">Payment Method</p>
                              <p className="text-white flex items-center gap-1">
                                <CreditCard className="w-4 h-4" />
                                {request.payment_method || 'N/A'}
                              </p>
                            </div>
                            <div className="bg-white/5 rounded p-3">
                              <p className="text-gray-400 text-xs mb-1">Requested Date</p>
                              <p className="text-white flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {new Date(request.requested_at).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="bg-white/5 rounded p-3">
                              <p className="text-gray-400 text-xs mb-1">Request ID</p>
                              <p className="text-white text-xs font-mono">{request.id?.substring(0, 8)}...</p>
                            </div>
                          </div>

                          {request.payment_details && (
                            <div className="bg-white/5 rounded p-3 mb-4">
                              <p className="text-gray-400 text-xs mb-1">Payment Details</p>
                              <p className="text-white text-sm whitespace-pre-wrap">{request.payment_details}</p>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col gap-2 min-w-[180px]">
                          <Button
                            onClick={() => setProcessingRequest(request.id)}
                            className="bg-blue-600 hover:bg-blue-700 w-full"
                          >
                            Process Request
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Processed Requests */}
          {processedRequests.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-4">Processed Requests</h2>
              <div className="space-y-4">
                {processedRequests.map((request) => (
                  <Card key={request.id} className="glass-dark border-white/10">
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                {getStatusIcon(request.status)}
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(request.status)}`}>
                                  {request.status.toUpperCase()}
                                </span>
                              </div>
                              <h3 className="text-white text-lg font-semibold">{request.tipster_name}</h3>
                              <p className="text-gray-400 text-sm">{request.tipster_email}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-white">
                                <Price amount={request.amount || 0} />
                              </p>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-white/5 rounded p-3">
                              <p className="text-gray-400 text-xs mb-1">Requested</p>
                              <p className="text-white text-sm">
                                {new Date(request.requested_at).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="bg-white/5 rounded p-3">
                              <p className="text-gray-400 text-xs mb-1">Processed</p>
                              <p className="text-white text-sm">
                                {request.processed_at ? new Date(request.processed_at).toLocaleDateString() : 'N/A'}
                              </p>
                            </div>
                          </div>

                          {request.admin_notes && (
                            <div className="bg-white/5 rounded p-3 mt-4">
                              <p className="text-gray-400 text-xs mb-1">Admin Notes</p>
                              <p className="text-white text-sm">{request.admin_notes}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {requests.length === 0 && (
            <Card className="glass-dark border-white/10">
              <CardContent className="py-12 text-center">
                <DollarSign className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No withdrawal requests found</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Processing Modal */}
      {processingRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="glass-dark border-white/10 max-w-md w-full">
            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                <span>Process Withdrawal</span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setProcessingRequest(null);
                    setAdminNotes('');
                  }}
                  className="text-white hover:bg-white/10"
                >
                  ✕
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-white mb-2">Admin Notes (Optional)</Label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add notes about this withdrawal request..."
                  className="bg-white/5 border-white/10 text-white"
                  rows={3}
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => handleProcess(processingRequest, 'approved')}
                  className="bg-green-600 hover:bg-green-700 flex-1"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
                <Button
                  onClick={() => handleProcess(processingRequest, 'rejected')}
                  variant="outline"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/20 flex-1"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Layout>
  );
};

export default AdminWithdrawals;
